/**
 * Cross-Sector Referral Service — Überweisungsportal
 *
 * Tabellen: referrals, referral_slots, referral_messages, referral_status_history,
 *           referral_consents, practice_referral_settings, referral_audit_log
 * RPCs: book_referral_appointment, can_send_referral, get_referral_inbox_full
 *
 * Reuses Supabase infrastructure from mobile/src/services/referralService.ts
 * but follows practice-web service pattern (named exports, no class).
 */

// TI-BOUNDARY: Dieses Modul orchestriert den Zuweisungs-Workflow.
// Es darf KEINE TI/KIM/ePA-Operationen enthalten.
// Export-Payloads (PDF, FHIR, GDT) werden erzeugt, aber NICHT über TI-Kanäle versendet.
// Siehe: docs/architecture/REFERRAL_COMPLIANCE_BOUNDARY.md
import { supabase, isSupabaseConfigured, requireSupabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type {
    Referral,
    ReferralStatus,
    ReferralUrgency,
    MedicalSpecialty,
    TreatmentOutcome,
    ReferralInboxItem,
    ReferralMessage,
    ReferralSlot,
    ReferralBooking,
    ReferralStatusEvent,
    AvailableSpecialist,
    PracticeReferralSettings,
    CreateReferralInput,
    SendReportInput,
    IncomingReport,
    BookSlotResult,
    ReferralExportEvent,
    ReferralExportFormat,
    ReferralExportTarget,
} from "@/types/referral";
import type { ReferralBundleInput, FhirReferralBundle } from "@shared/fhir/referral-bundle";
import { buildReferralFhirBundle } from "@shared/fhir/referral-bundle";
import {
    ACTIVE_INBOX_REFERRAL_STATUSES,
    SPECIALTY_LABELS,
    UNREAD_INCOMING_REFERRAL_STATUSES,
} from "@/types/referral";

type ReferralCommandResult = {
    success?: boolean;
    error?: string;
};

function getCommandResult(data: unknown): ReferralCommandResult {
    const row = getSingleRpcRecord(data);
    if (!row) return {};

    const success = (row as { success?: unknown }).success;
    const error = (row as { error?: unknown }).error;

    return {
        success: typeof success === "boolean" ? success : undefined,
        error: typeof error === "string" ? error : undefined,
    };
}

// ─── DB → Domain Mappers ────────────────────────────────────

function mapDbToReferral(data: Record<string, unknown>): Referral {
    const id = data.id as string;
    return {
        id,
        referralNumber:
            (data.referral_number as string) ?? `REF-${id?.substring(0, 8).toUpperCase()}`,
        sourcePracticeId: data.source_practice_id as string,
        sourcePracticeName: (data as Record<string, unknown>).source_practice
            ? ((data as Record<string, unknown>).source_practice as Record<string, unknown>)
                  ?.name as string
            : undefined,
        sourceDoctorId: (data.source_doctor_id as string) ?? null,
        targetPracticeId: (data.target_practice_id as string) ?? null,
        targetPracticeName: (data as Record<string, unknown>).target_practice
            ? ((data as Record<string, unknown>).target_practice as Record<string, unknown>)
                  ?.name as string
            : undefined,
        targetDoctorId: (data.target_doctor_id as string) ?? null,
        patientId: data.patient_id as string,
        patientName: data.patients
            ? `${(data.patients as Record<string, unknown>).first_name ?? ""} ${(data.patients as Record<string, unknown>).last_name ?? ""}`.trim()
            : undefined,
        specialty: data.specialty as MedicalSpecialty,
        urgency: data.urgency as ReferralUrgency,
        status: data.status as ReferralStatus,
        diagnosisText: (data.diagnosis_text as string) ?? null,
        reasonForReferral: (data.referral_reason as string) ?? (data.clinical_question as string) ?? null,
        clinicalQuestion: (data.clinical_question as string) ?? null,
        relevantHistory: (data.relevant_findings as string) ?? null,
        attachmentIds: [],
        specialistNotes: (data.specialist_notes as string) ?? null,
        specialistReportText: (data.specialist_report_text as string) ?? null,
        specialistReportDate: (data.specialist_report_date as string) ?? null,
        treatmentOutcome: (data.treatment_outcome as TreatmentOutcome) ?? null,
        reportReadAt: (data.report_read_at as string) ?? null,
        reportReadBy: (data.report_read_by as string) ?? null,
        accessToken: (data.access_token as string) ?? null,
        appointmentId: (data.appointment_id as string) ?? (data.booked_appointment_id as string) ?? null,
        expiresAt: (data.valid_until as string) ?? null,
        createdAt: data.created_at as string,
        updatedAt: data.updated_at as string,
    };
}

function mapDbToInboxItem(row: Record<string, unknown>): ReferralInboxItem {
    const id = row.id as string;
    const patients = row.patients as Record<string, unknown> | null;
    const sourcePractice = row.source_practice as Record<string, unknown> | null;
    return {
        id,
        referralNumber:
            (row.referral_number as string) ?? `REF-${id?.substring(0, 8).toUpperCase()}`,
        patientId: row.patient_id as string,
        patientName: patients
            ? `${patients.first_name ?? ""} ${patients.last_name ?? ""}`.trim()
            : "Unbekannter Patient",
        sourcePracticeId: row.source_practice_id as string,
        sourcePracticeName: (sourcePractice?.name as string) ?? "Unbekannte Praxis",
        targetPracticeId: (row.target_practice_id as string) ?? "",
        specialty: row.specialty as MedicalSpecialty,
        urgency: row.urgency as ReferralUrgency,
        status: row.status as ReferralStatus,
        diagnosisText: (row.diagnosis_text as string) ?? null,
        reasonForReferral: (row.referral_reason as string) ?? null,
        createdAt: row.created_at as string,
    };
}

function mapDbToMessage(data: Record<string, unknown>): ReferralMessage {
    return {
        id: data.id as string,
        referralId: data.referral_id as string,
        senderPracticeId: data.sender_practice_id as string,
        senderPracticeName: (data.sender_practice as Record<string, unknown>)?.name as
            | string
            | undefined,
        messageText: data.message_text as string,
        attachmentIds: [],
        isRead: (data.is_read as boolean) ?? false,
        createdAt: data.created_at as string,
    };
}

function mapDbToSlot(data: Record<string, unknown>): ReferralSlot {
    return {
        id: data.id as string,
        practiceId: data.practice_id as string,
        startTime: data.start_time as string,
        endTime: data.end_time as string,
        isAvailable: (data.is_available as boolean) ?? true,
        doctorId: (data.doctor_id as string) ?? null,
    };
}

function mapDbToStatusEvent(data: Record<string, unknown>): ReferralStatusEvent {
    return {
        id: data.id as string,
        referralId: data.referral_id as string,
        previousStatus: (data.previous_status as ReferralStatus) ?? null,
        newStatus: data.new_status as ReferralStatus,
        changedByUserId: (data.changed_by_user_id as string) ?? null,
        changedByPracticeId: (data.changed_by_practice_id as string) ?? null,
        reason: (data.reason as string) ?? null,
        timestamp: (data.timestamp as string) ?? (data.created_at as string),
    };
}

function mapDbToPracticeSettings(data: Record<string, unknown>): PracticeReferralSettings {
    return {
        practiceId: data.practice_id as string,
        acceptsReferrals: (data.accepts_referrals as boolean) ?? true,
        maxReferralsPerDay: (data.max_referrals_per_day as number) ?? null,
        acceptedSpecialties: (data.accepted_specialties as MedicalSpecialty[]) ?? [],
        vacationMode: (data.vacation_mode as boolean) ?? false,
        vacationUntil: (data.referral_pause_until as string) ?? null,
        autoAcceptRoutine: (data.auto_accept_from_partners as boolean) ?? false,
        notifyOnNewReferral: (data.notify_new_referral_email as boolean) ?? true,
    };
}

function isRpcRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function unwrapRpcPayload(value: unknown, depth = 0): unknown {
    if (depth > 4 || value == null) return null;

    if (Array.isArray(value)) {
        if (value.length === 0) return null;
        return unwrapRpcPayload(value[0], depth + 1);
    }

    if (!isRpcRecord(value)) {
        return null;
    }

    const keys = Object.keys(value);
    if (keys.length === 1) {
        const wrapperKey = keys[0];
        if (!wrapperKey) return value;

        const nested = value[wrapperKey];
        if (Array.isArray(nested) || isRpcRecord(nested)) {
            return unwrapRpcPayload(nested, depth + 1);
        }
    }

    return value;
}

function getSingleRpcRecord(data: unknown): Record<string, unknown> | null {
    const unwrapped = unwrapRpcPayload(data);
    return isRpcRecord(unwrapped) ? unwrapped : null;
}

type DynamicSlotBookingMeta = Pick<ReferralSlot, "practiceId" | "startTime" | "endTime">;

function parseDynamicSlotId(slotId: string): { practiceId: string; startTime: string } | null {
    const match = slotId.match(/^dynamic-(.+?)-(\d{4}-\d{2}-\d{2}T.+)$/);
    if (!match) return null;

    const practiceId = match[1];
    const startTime = match[2];
    if (!practiceId || !startTime) return null;

    return { practiceId, startTime };
}

function resolveDynamicSlotBookingMeta(
    slotId: string,
    slotMeta?: DynamicSlotBookingMeta,
): DynamicSlotBookingMeta | null {
    const parsed = parseDynamicSlotId(slotId);
    const practiceId = slotMeta?.practiceId ?? parsed?.practiceId;
    const startTimeRaw = slotMeta?.startTime ?? parsed?.startTime;
    if (!practiceId || !startTimeRaw) return null;

    const startDate = new Date(startTimeRaw);
    if (Number.isNaN(startDate.getTime())) return null;

    let endDate = new Date(startDate.getTime() + 30 * 60000);
    if (slotMeta?.endTime) {
        const candidateEndDate = new Date(slotMeta.endTime);
        if (!Number.isNaN(candidateEndDate.getTime()) && candidateEndDate > startDate) {
            endDate = candidateEndDate;
        }
    }

    return {
        practiceId,
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
    };
}

function normalizeOptionalText(value?: string | null): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
}

async function isReferralMessageRelevantToPractice(
    referralId: string,
    practiceId: string,
): Promise<boolean> {
    try {
        const { data, error } = await supabase
            .from("referrals")
            .select("id")
            .eq("id", referralId)
            .or(`source_practice_id.eq.${practiceId},target_practice_id.eq.${practiceId}`)
            .maybeSingle();

        if (error) {
            if (error.code === "PGRST116") return false;
            throw error;
        }

        return !!data;
    } catch (e) {
        logger.error("referral.isReferralMessageRelevantToPractice", e);
        return false;
    }
}

// ─── INBOX (eingehende Überweisungen) ───────────────────────

export async function getInbox(
    practiceId: string,
    statusFilter?: ReferralStatus[],
    limit = 50,
): Promise<ReferralInboxItem[]> {
    if (!isSupabaseConfigured()) return [];
    try {
        const statuses = statusFilter ?? [...ACTIVE_INBOX_REFERRAL_STATUSES];
        const { data, error } = await supabase
            .from("referrals")
            .select(
                `*, patients:patient_id (first_name, last_name), source_practice:source_practice_id (name)`,
            )
            .eq("target_practice_id", practiceId)
            .in("status", statuses)
            .order("created_at", { ascending: false })
            .limit(limit);

        if (error) throw error;
        return (data ?? []).map((r: Record<string, unknown>) => mapDbToInboxItem(r));
    } catch (e) {
        logger.error("referral.getInbox", e);
        return [];
    }
}

// ─── OUTBOX (ausgehende Überweisungen) ──────────────────────

export async function getOutgoingReferrals(practiceId: string): Promise<Referral[]> {
    if (!isSupabaseConfigured()) return [];
    try {
        const { data, error } = await supabase
            .from("referrals")
            .select(
                `*, patients:patient_id (first_name, last_name), target_practice:target_practice_id (name)`,
            )
            .eq("source_practice_id", practiceId)
            .order("created_at", { ascending: false });

        if (error) throw error;
        return (data ?? []).map((r: Record<string, unknown>) => mapDbToReferral(r));
    } catch (e) {
        logger.error("referral.getOutgoingReferrals", e);
        return [];
    }
}

// ─── SINGLE REFERRAL ────────────────────────────────────────

export async function getReferral(referralId: string): Promise<Referral | null> {
    if (!isSupabaseConfigured()) return null;
    try {
        const { data, error } = await supabase
            .from("referrals")
            .select(
                `*, patients:patient_id (first_name, last_name), source_practice:source_practice_id (name, phone, email), target_practice:target_practice_id (name, phone, email, street, postal_code, city)`,
            )
            .eq("id", referralId)
            .single();

        if (error) {
            if (error.code === "PGRST116") return null;
            throw error;
        }
        return mapDbToReferral(data as Record<string, unknown>);
    } catch (e) {
        logger.error("referral.getReferral", e);
        return null;
    }
}

// ─── CREATE ─────────────────────────────────────────────────

export async function createReferral(input: CreateReferralInput): Promise<Referral | null> {
    try {
        const validUntil = new Date();
        validUntil.setDate(validUntil.getDate() + 90);

        const sb = requireSupabase();
        const { data, error } = await sb.rpc("create_referral", {
            p_source_practice_id: input.sourcePracticeId,
            p_source_doctor_id: input.sourceDoctorId ?? null,
            p_patient_id: input.patientId,
            p_target_practice_id: input.targetPracticeId ?? null,
            p_specialty: input.specialty,
            p_urgency: input.urgency,
            p_diagnosis_text: normalizeOptionalText(input.diagnosisText),
            p_clinical_question: normalizeOptionalText(input.clinicalQuestion),
            p_referral_reason: normalizeOptionalText(input.reasonForReferral),
            p_relevant_findings: normalizeOptionalText(input.relevantHistory),
            p_valid_until: validUntil.toISOString(),
        });

        if (error) throw error;
        const row = getSingleRpcRecord(data);
        if (!row) return null;

        const referral = mapDbToReferral(row);

        return referral;
    } catch (e) {
        logger.error("referral.createReferral", e);
        return null;
    }
}

// ─── UPDATE ─────────────────────────────────────────────────

export async function updateReferralStatus(
    referralId: string,
    status: ReferralStatus,
    extra?: Record<string, unknown>,
): Promise<boolean> {
    try {
        const sb = requireSupabase();

        if (status === "confirmed") {
            const { data, error } = await sb.rpc("accept_referral", {
                p_referral_id: referralId,
                p_target_doctor_id: (extra?.targetDoctorId as string | null | undefined) ?? null,
                p_notes: (extra?.specialistNotes as string | null | undefined) ?? null,
            });

            if (error) throw error;
            const result = getCommandResult(data);
            if (!result.success) {
                throw new Error(result.error ?? "REFERRAL_ACCEPT_FAILED");
            }
            return true;
        }

        if (status === "cancelled") {
            const { data, error } = await sb.rpc("reject_referral", {
                p_referral_id: referralId,
                p_reason:
                    (extra?.cancellation_reason as string | null | undefined) ??
                    (extra?.reason as string | null | undefined) ??
                    null,
            });

            if (error) throw error;
            const result = getCommandResult(data);
            if (!result.success) {
                throw new Error(result.error ?? "REFERRAL_REJECT_FAILED");
            }
            return true;
        }

        if (status === "sent") {
            const { data, error } = await sb.rpc("update_referral_signature_status", {
                p_referral_id: referralId,
                p_new_status: "sent",
            });
            if (error) throw error;
            const result = getCommandResult(data);
            if (!result.success) throw new Error(result.error ?? "REFERRAL_SEND_FAILED");
            return true;
        }

        // Generic status transitions (in_treatment, completed) — direct update with history insert
        if (status === "in_treatment" || status === "completed") {
            const { error: updErr } = await sb
                .from("referrals")
                .update({ status, updated_at: new Date().toISOString() })
                .eq("id", referralId);
            if (updErr) throw updErr;

            // Record in status history
            await sb.from("referral_status_history").insert({
                referral_id: referralId,
                previous_status: extra?.previousStatus ?? null,
                new_status: status,
                changed_by_user_id: (extra?.userId as string) ?? null,
                changed_by_practice_id: (extra?.practiceId as string) ?? null,
                reason: (extra?.reason as string) ?? null,
            });
            return true;
        }

        logger.warn("referral.updateReferralStatus.unsupportedStatus", status);
        return false;
    } catch (e) {
        logger.error("referral.updateReferralStatus", e);
        return false;
    }
}

/**
 * Send a referral (transition pending → sent).
 * Uses the P2 command layer RPC `update_referral_signature_status`.
 */
export async function sendReferral(referralId: string): Promise<boolean> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("update_referral_signature_status", {
            p_referral_id: referralId,
            p_new_status: "sent",
            p_cancellation_reason: null,
        });
        if (error) throw error;
        const result = getCommandResult(data);
        if (!result.success) throw new Error(result.error ?? "REFERRAL_SEND_FAILED");
        return true;
    } catch (e) {
        logger.error("referral.sendReferral", e);
        return false;
    }
}

export async function cancelReferral(referralId: string, reason: string): Promise<boolean> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("cancel_referral", {
            p_referral_id: referralId,
            p_reason: reason,
        });
        if (error) throw error;
        const result = getCommandResult(data);
        if (result && !result.success) throw new Error(result.error ?? "CANCEL_FAILED");
        return true;
    } catch (e) {
        logger.error("referral.cancelReferral", e);
        return false;
    }
}

// ─── SPECIALIST SEARCH ──────────────────────────────────────

export async function findAvailableSpecialists(
    specialty: MedicalSpecialty,
    myPracticeId?: string,
): Promise<AvailableSpecialist[]> {
    if (!isSupabaseConfigured()) return [];
    try {
        const displayName = SPECIALTY_LABELS[specialty] ?? specialty;

        // Strangler Fig: Also check practice_specialties junction table
        // to find multi-specialty practices, not just primary specialty
        const { data: junctionMatches } = await supabase
            .from("practice_specialties")
            .select("practice_id")
            .ilike("specialty_name", displayName)
            .eq("is_active", true);

        const junctionPracticeIds = (junctionMatches ?? []).map(
            (m: Record<string, unknown>) => m.practice_id as string,
        );

        // Query practices: match primary specialty OR junction table entries
        let practiceQuery = supabase
            .from("practices")
            .select("*")
            .eq("accepting_referrals", true)
                .in("registration_status", ["active", "approved"]);

        if (junctionPracticeIds.length > 0) {
            practiceQuery = practiceQuery.or(
                `specialty.ilike.${displayName},id.in.(${junctionPracticeIds.join(",")})`,
            );
        } else {
            practiceQuery = practiceQuery.ilike("specialty", displayName);
        }

        const { data, error } = await practiceQuery;

        if (error) throw error;
        if (!data?.length) return [];

        const practiceIds = data.map((p: Record<string, unknown>) => p.id as string);
        const nowDate = new Date();
        const now = nowDate.toISOString();
        const twoWeeksLater = new Date(nowDate.getTime() + 14 * 86400000).toISOString();

        // Slot counts (pre-generated)
        const { data: slotCounts } = await supabase
            .from("referral_slots")
            .select("practice_id")
            .in("practice_id", practiceIds)
            .eq("is_available", true)
            .gte("start_time", now);

        const slotMap = new Map<string, number>();
        for (const s of slotCounts ?? []) {
            const pid = (s as Record<string, unknown>).practice_id as string;
            slotMap.set(pid, (slotMap.get(pid) ?? 0) + 1);
        }

        // Parity fallback: if no pre-generated referral_slots exist,
        // estimate via the same dynamic working-hours logic used for slot booking.
        const practicesWithoutPreGeneratedSlots = practiceIds.filter((pid) => !slotMap.has(pid));
        if (practicesWithoutPreGeneratedSlots.length > 0) {
            const fallbackCounts = await Promise.all(
                practicesWithoutPreGeneratedSlots.map(async (pid) => {
                    const dynamicSlots = await generateSlotsFromWorkingHours(pid, now, twoWeeksLater);
                    return { pid, count: dynamicSlots.length };
                }),
            );

            for (const entry of fallbackCounts) {
                if (entry.count > 0) {
                    slotMap.set(entry.pid, entry.count);
                }
            }
        }

        // Partner-Lookup: Check which of the found practices are active network partners
        const partnerSet = new Set<string>();
        if (myPracticeId && practiceIds.length > 0) {
            const { data: partnerLinks } = await supabase
                .from("organization_partnerships")
                .select("requester_practice_id, target_practice_id")
                .eq("status", "AKTIV")
                .or(
                    `and(requester_practice_id.eq.${myPracticeId},target_practice_id.in.(${practiceIds.join(",")})),and(target_practice_id.eq.${myPracticeId},requester_practice_id.in.(${practiceIds.join(",")}))`,
                );

            for (const link of partnerLinks ?? []) {
                const row = link as Record<string, unknown>;
                const partnerId = row.requester_practice_id === myPracticeId
                    ? row.target_practice_id as string
                    : row.requester_practice_id as string;
                partnerSet.add(partnerId);
            }
        }

        const results = data.map((p: Record<string, unknown>): AvailableSpecialist => {
            const parts = [p.street, p.postal_code, p.city].filter(Boolean);
            return {
                practiceId: (p.id ?? p.practice_id) as string,
                practiceName: (p.name ?? p.practice_name ?? "Unbekannte Praxis") as string,
                specialty: p.specialty as MedicalSpecialty,
                address: parts.length ? parts.join(", ") : null,
                city: (p.city as string) ?? null,
                distance: null,
                phone: (p.phone as string) ?? null,
                availableSlotCount: slotMap.get(p.id as string) ?? 0,
                acceptingReferrals: true,
                nextAvailableSlot: null,
                averageRating: (p.rating as number) ?? null,
                isNetworkPartner: partnerSet.has(p.id as string),
            };
        });

        // Partner zuerst, dann nach verfuegbaren Slots sortieren
        results.sort((a, b) => {
            if (a.isNetworkPartner !== b.isNetworkPartner) {
                return a.isNetworkPartner ? -1 : 1;
            }
            return b.availableSlotCount - a.availableSlotCount;
        });

        return results;
    } catch (e) {
        logger.error("referral.findAvailableSpecialists", e);
        return [];
    }
}

// ─── SLOTS & BOOKING ────────────────────────────────────────

export async function getAvailableSlots(
    practiceId: string,
    startDate?: string,
    endDate?: string,
): Promise<ReferralSlot[]> {
    if (!isSupabaseConfigured()) return [];
    try {
        const now = new Date();
        const start = startDate ?? now.toISOString();
        const end = endDate ?? new Date(now.getTime() + 14 * 86400000).toISOString();

        // 1) Pre-generated referral_slots
        const { data, error } = await supabase
            .from("referral_slots")
            .select("*")
            .eq("practice_id", practiceId)
            .eq("is_available", true)
            .gte("start_time", start)
            .lte("start_time", end)
            .order("start_time", { ascending: true });

        if (!error && data?.length) {
            return data.map((s: Record<string, unknown>) => mapDbToSlot(s));
        }

        // 2) Fallback — dynamic from practice_working_hours
        return generateSlotsFromWorkingHours(practiceId, start, end);
    } catch (e) {
        logger.error("referral.getAvailableSlots", e);
        return [];
    }
}

async function generateSlotsFromWorkingHours(
    practiceId: string,
    startStr: string,
    endStr: string,
    duration = 30,
): Promise<ReferralSlot[]> {
    const slots: ReferralSlot[] = [];
    const now = new Date();
    try {
        const { data: hours } = await supabase
            .from("practice_working_hours")
            .select("practice_id, day_of_week, start_time, end_time")
            .eq("practice_id", practiceId)
            .eq("is_active", true);

        if (!hours?.length) return [];

        const { data: appointments } = await supabase
            .from("appointments")
            .select("start_time, end_time")
            .eq("practice_id", practiceId)
            .gte("start_time", startStr)
            .lte("start_time", endStr)
            .neq("status", "cancelled");

        const byDay = new Map<number, Array<{ start: string; end: string }>>();
        for (const wh of hours) {
            const whr = wh as Record<string, unknown>;
            const arr = byDay.get(whr.day_of_week as number) ?? [];
            arr.push({ start: whr.start_time as string, end: whr.end_time as string });
            byDay.set(whr.day_of_week as number, arr);
        }

        const cur = new Date(startStr);
        const endD = new Date(endStr);
        while (cur <= endD) {
            const dayHours = byDay.get(cur.getDay());
            if (dayHours) {
                for (const h of dayHours) {
                    const parts = h.start.split(":").map(Number);
                    const endParts = h.end.split(":").map(Number);
                    const sh = parts[0] ?? 0;
                    const sm = parts[1] ?? 0;
                    const eh = endParts[0] ?? 0;
                    const em = endParts[1] ?? 0;
                    const slotStart = new Date(cur);
                    slotStart.setHours(sh, sm, 0, 0);
                    const periodEnd = new Date(cur);
                    periodEnd.setHours(eh, em, 0, 0);

                    while (slotStart < periodEnd) {
                        const slotEnd = new Date(slotStart.getTime() + duration * 60000);
                        if (slotStart > now) {
                            const booked = (appointments ?? []).some((a: Record<string, unknown>) => {
                                const as2 = new Date(a.start_time as string);
                                const ae = new Date(a.end_time as string);
                                return slotStart < ae && slotEnd > as2;
                            });
                            if (!booked) {
                                slots.push({
                                    id: `dynamic-${practiceId}-${slotStart.toISOString()}`,
                                    practiceId,
                                    startTime: slotStart.toISOString(),
                                    endTime: slotEnd.toISOString(),
                                    isAvailable: true,
                                    doctorId: null,
                                });
                            }
                        }
                        slotStart.setTime(slotStart.getTime() + duration * 60000);
                    }
                }
            }
            cur.setDate(cur.getDate() + 1);
        }
        return slots;
    } catch (e) {
        logger.error("referral.generateSlotsFromWorkingHours", e);
        return [];
    }
}

export async function bookSlot(
    referralId: string,
    slotId: string,
    userId?: string,
    dynamicSlotMeta?: DynamicSlotBookingMeta,
): Promise<BookSlotResult> {
    try {
        const sb = requireSupabase();

        // Dynamic slot → manual booking
        if (slotId.startsWith("dynamic-")) {
            const booking = await bookDynamicSlot(referralId, slotId, userId, dynamicSlotMeta);
            if (!booking) return { ok: false, reason: "Buchung fehlgeschlagen" };
            return { ok: true, booking };
        }

        // Pre-generated → atomic RPC
        const { data, error } = await sb.rpc("book_referral_appointment", {
            p_referral_id: referralId,
            p_slot_id: slotId,
            p_booked_by_user_id: userId ?? null,
        });
        if (error) {
            logger.error("referral.bookSlot.rpc", error);
            return { ok: false, reason: error.message };
        }

        const result = getSingleRpcRecord(data) as {
            success?: boolean;
            error?: string;
            message?: string;
            appointment_id?: string;
        } | null;
        if (!result?.success) {
            const reason = result?.message ?? result?.error ?? "Slot nicht mehr verfügbar";
            logger.warn("referral.bookSlot.rpc_rejected", { reason });
            return { ok: false, reason };
        }

        return {
            ok: true,
            booking: {
                referralId,
                appointmentId: result.appointment_id ?? null,
                slotId,
                bookedAt: new Date().toISOString(),
            },
        };
    } catch (e) {
        const msg = e instanceof Error ? e.message : "Unbekannter Fehler";
        logger.error("referral.bookSlot", e);
        return { ok: false, reason: msg };
    }
}

async function bookDynamicSlot(
    referralId: string,
    slotId: string,
    _userId?: string,
    slotMeta?: DynamicSlotBookingMeta,
): Promise<ReferralBooking | null> {
    const sb = requireSupabase();
    const bookingMeta = resolveDynamicSlotBookingMeta(slotId, slotMeta);
    if (!bookingMeta) {
        throw new Error("Ungültiges Slot-ID Format");
    }

    // Command Layer RPC — atomic: conflict check + appointment + referral update + patient registration
    const { data, error } = await sb.rpc("book_referral_dynamic_slot", {
        p_referral_id: referralId,
        p_practice_id: bookingMeta.practiceId,
        p_start_time: bookingMeta.startTime,
        p_end_time: bookingMeta.endTime,
    });

    if (error) throw error;

    const result = getSingleRpcRecord(data) as ReferralCommandResult & {
        appointment_id?: string;
    } | null;

    if (!result?.success) {
        throw new Error((result as Record<string, unknown>)?.message as string
            || result?.error || "Buchung fehlgeschlagen");
    }

    return {
        referralId,
        appointmentId: result.appointment_id ?? null,
        slotId,
        bookedAt: new Date().toISOString(),
    };
}

// ─── MESSAGING ──────────────────────────────────────────────

export async function getMessages(referralId: string): Promise<ReferralMessage[]> {
    if (!isSupabaseConfigured()) return [];
    try {
        const { data, error } = await supabase
            .from("referral_messages")
            .select("*")
            .eq("referral_id", referralId)
            .order("created_at", { ascending: true });
        if (error) throw error;
        return (data ?? []).map((m: Record<string, unknown>) => mapDbToMessage(m));
    } catch (e) {
        logger.error("referral.getMessages", e);
        return [];
    }
}

export async function sendMessage(
    referralId: string,
    senderPracticeId: string,
    text: string,
): Promise<ReferralMessage | null> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("send_referral_message", {
            p_referral_id: referralId,
            p_sender_practice_id: senderPracticeId,
            p_message_text: text,
        });
        if (error) throw error;
        const row = getSingleRpcRecord(data);
        return row ? mapDbToMessage(row) : null;
    } catch (e) {
        logger.error("referral.sendMessage", e);
        return null;
    }
}

export async function markMessagesRead(
    referralId: string,
    practiceId: string,
): Promise<boolean> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("mark_referral_messages_read", {
            p_referral_id: referralId,
            p_reader_practice_id: practiceId,
        });
        if (error) throw error;
        const result = getCommandResult(data);
        if (result && !result.success) throw new Error(result.error ?? "MARK_MESSAGES_READ_FAILED");
        return true;
    } catch (e) {
        logger.error("referral.markMessagesRead", e);
        return false;
    }
}

// ─── REPORT-BACK (Facharzt → Hausarzt) ─────────────────────

export async function sendSpecialistReport(input: SendReportInput): Promise<boolean> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("send_specialist_report", {
            p_referral_id: input.referralId,
            p_report_text: input.reportText,
            p_treatment_outcome: input.treatmentOutcome ?? null,
            p_document_ids: input.documentIds ?? null,
            p_follow_up_required: (input as unknown as Record<string, unknown>).followUpRequired ?? false,
            p_follow_up_weeks: (input as unknown as Record<string, unknown>).followUpWeeks ?? null,
        });
        if (error) throw error;
        const result = getCommandResult(data);
        if (result && !result.success) throw new Error(result.error ?? "SEND_REPORT_FAILED");
        return true;
    } catch (e) {
        logger.error("referral.sendSpecialistReport", e);
        return false;
    }
}

export async function getIncomingReports(practiceId: string): Promise<IncomingReport[]> {
    if (!isSupabaseConfigured()) return [];
    try {
        const { data, error } = await supabase
            .from("referrals")
            .select(
                `*, patients:patient_id (id, first_name, last_name), target_practice:target_practice_id (id, name)`,
            )
            .eq("source_practice_id", practiceId)
            .not("specialist_report_date", "is", null)
            .order("specialist_report_date", { ascending: false });
        if (error) throw error;

        return (data ?? []).map((row: Record<string, unknown>): IncomingReport => {
            const patients = row.patients as Record<string, unknown> | null;
            const tp = row.target_practice as Record<string, unknown> | null;
            return {
                id: row.id as string,
                referralNumber:
                    (row.referral_number as string) ??
                    `REF-${(row.id as string)?.substring(0, 8).toUpperCase()}`,
                patientId: row.patient_id as string,
                patientName: patients
                    ? `${patients.first_name ?? ""} ${patients.last_name ?? ""}`.trim()
                    : "Unbekannter Patient",
                targetPracticeId: (row.target_practice_id as string) ?? "",
                targetPracticeName: (tp?.name as string) ?? "Unbekannte Praxis",
                specialty: row.specialty as MedicalSpecialty,
                treatmentOutcome: (row.treatment_outcome as TreatmentOutcome) ?? null,
                specialistReportText: (row.specialist_report_text as string) ?? null,
                specialistReportDate: (row.specialist_report_date as string) ?? null,
                reportReadAt: (row.report_read_at as string) ?? null,
                createdAt: row.created_at as string,
            };
        });
    } catch (e) {
        logger.error("referral.getIncomingReports", e);
        return [];
    }
}

export async function getUnreadReportsCount(practiceId: string): Promise<number> {
    if (!isSupabaseConfigured()) return 0;
    try {
        const { count, error } = await supabase
            .from("referrals")
            .select("id", { count: "exact" })
            .eq("source_practice_id", practiceId)
            .not("specialist_report_date", "is", null)
            .is("report_read_at", null)
            .limit(1);
        if (error) throw error;
        return count ?? 0;
    } catch (e) {
        logger.error("referral.getUnreadReportsCount", e);
        return 0;
    }
}

export async function markReportAsRead(
    referralId: string,
    _userId: string,
): Promise<boolean> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("mark_report_read", {
            p_referral_id: referralId,
        });
        if (error) throw error;
        const result = getCommandResult(data);
        if (result && !result.success) throw new Error(result.error ?? "MARK_READ_FAILED");
        return true;
    } catch (e) {
        logger.error("referral.markReportAsRead", e);
        return false;
    }
}

// ─── STATUS HISTORY ─────────────────────────────────────────

export async function getStatusHistory(referralId: string): Promise<ReferralStatusEvent[]> {
    if (!isSupabaseConfigured()) return [];
    try {
        const { data, error } = await supabase
            .from("referral_status_history")
            .select("*")
            .eq("referral_id", referralId)
            .order("timestamp", { ascending: true });
        if (error) throw error;
        return (data ?? []).map((e: Record<string, unknown>) => mapDbToStatusEvent(e));
    } catch (e) {
        logger.error("referral.getStatusHistory", e);
        return [];
    }
}

// ─── SETTINGS ───────────────────────────────────────────────

export async function getPracticeReferralSettings(
    practiceId: string,
): Promise<PracticeReferralSettings | null> {
    if (!isSupabaseConfigured()) return null;
    try {
        const { data, error } = await supabase
            .from("practice_referral_settings")
            .select("*")
            .eq("practice_id", practiceId)
            .maybeSingle();

        if (error) throw error;
        return data ? mapDbToPracticeSettings(data as Record<string, unknown>) : null;
    } catch (e) {
        logger.error("referral.getPracticeReferralSettings", e);
        return null;
    }
}

export async function updatePracticeReferralSettings(
    practiceId: string,
    settings: Partial<PracticeReferralSettings>,
): Promise<boolean> {
    try {
        const sb = requireSupabase();
        const { error } = await sb
            .from("practice_referral_settings")
            .upsert(
                {
                    practice_id: practiceId,
                    accepts_referrals: settings.acceptsReferrals,
                    max_referrals_per_day: settings.maxReferralsPerDay,
                    accepted_specialties: settings.acceptedSpecialties,
                    vacation_mode: settings.vacationMode,
                    referral_pause_until: settings.vacationUntil,
                    auto_accept_from_partners: settings.autoAcceptRoutine,
                    notify_new_referral_email: settings.notifyOnNewReferral,
                    updated_at: new Date().toISOString(),
                },
                { onConflict: "practice_id" },
            )
            .select()
            .single();
        if (error) throw error;
        return true;
    } catch (e) {
        logger.error("referral.updatePracticeReferralSettings", e);
        return false;
    }
}

// ─── CONSENT (DSGVO) ───────────────────────────────────────

export async function grantReferralConsent(
    referralId: string,
    patientId: string,
    consentType: "data_transfer" | "document_sharing" | "contact_permission",
): Promise<boolean> {
    try {
        const sb = requireSupabase();
        const { error } = await sb.from("referral_consents").upsert(
            {
                referral_id: referralId,
                patient_id: patientId,
                consent_type: consentType,
                granted_at: new Date().toISOString(),
                granted_via: "app",
            },
            { onConflict: "referral_id,consent_type" },
        );
        if (error) throw error;
        return true;
    } catch (e) {
        logger.error("referral.grantReferralConsent", e);
        return false;
    }
}

export interface ReferralConsentInfo {
    consentType: string;
    sharedCategories: string[];
    grantedAt: string | null;
    revokedAt: string | null;
}

const CATEGORY_LABELS: Record<string, string> = {
    personal: "Persönliche Daten",
    insurance: "Versicherung",
    medical_history: "Krankengeschichte",
    allergies: "Allergien",
    medications: "Medikamente",
    vaccinations: "Impfungen",
    lab_values: "Laborwerte",
    vital_signs: "Vitalwerte",
};

export { CATEGORY_LABELS };

export async function getReferralConsent(
    referralId: string,
): Promise<ReferralConsentInfo | null> {
    if (!isSupabaseConfigured()) return null;
    try {
        const { data, error } = await supabase
            .from("referral_consents")
            .select("consent_type, shared_categories, granted_at, revoked_at")
            .eq("referral_id", referralId)
            .eq("consent_type", "data_transfer")
            .maybeSingle();
        if (error) throw error;
        if (error || !data) return null;
        return {
            consentType: data.consent_type,
            sharedCategories: data.shared_categories ?? [],
            grantedAt: data.granted_at,
            revokedAt: data.revoked_at,
        };
    } catch (e) {
        logger.error("referral.getReferralConsent", e);
        return null;
    }
}

// ─── UNREAD COUNT ───────────────────────────────────────────

export async function getUnreadCount(practiceId: string): Promise<number> {
    if (!isSupabaseConfigured()) return 0;
    try {
        const { count, error } = await supabase
            .from("referrals")
            .select("id", { count: "exact" })
            .eq("target_practice_id", practiceId)
            .in("status", [...UNREAD_INCOMING_REFERRAL_STATUSES])
            .limit(1);
        if (error) throw error;
        return count ?? 0;
    } catch (e) {
        logger.error("referral.getUnreadCount", e);
        return 0;
    }
}

// ─── REALTIME SUBSCRIPTION ──────────────────────────────────

export function subscribeToReferrals(practiceId: string, onUpdate: () => void): () => void {
    if (!isSupabaseConfigured()) return () => {};

    const channel = supabase
        .channel(`referrals-${practiceId}`)
        .on(
            "postgres_changes",
            {
                event: "*",
                schema: "public",
                table: "referrals",
                filter: `target_practice_id=eq.${practiceId}`,
            },
            () => onUpdate(),
        )
        .on(
            "postgres_changes",
            {
                event: "*",
                schema: "public",
                table: "referrals",
                filter: `source_practice_id=eq.${practiceId}`,
            },
            () => onUpdate(),
        )
        .on(
            "postgres_changes",
            {
                event: "INSERT",
                schema: "public",
                table: "referral_messages",
            },
            (payload) => {
                const referralId = payload.new?.referral_id;
                if (typeof referralId !== "string") return;

                void isReferralMessageRelevantToPractice(referralId, practiceId).then(
                    (isRelevant) => {
                        if (isRelevant) onUpdate();
                    },
                );
            },
        )
        .subscribe();

    return () => {
        supabase.removeChannel(channel);
    };
}

// ============================================================================
// GAP-9: Patient Snapshot Import
// ============================================================================

export interface ImportPatientDataResult {
    success: boolean;
    patientId?: string;
    wasExisting?: boolean;
    importCount?: number;
    error?: string;
}

export async function importPatientData(
    referralId: string,
    userId: string,
): Promise<ImportPatientDataResult> {
    if (!isSupabaseConfigured()) {
        return { success: false, error: "NOT_CONFIGURED" };
    }

    const { data, error } = await supabase.rpc("import_referral_patient_data", {
        p_referral_id: referralId,
        p_target_user_id: userId,
    });

    if (error) {
        console.error("[ReferralService] importPatientData error:", error);
        return { success: false, error: error.message };
    }

    return data as ImportPatientDataResult;
}

// Legacy GAP-7a (practice_network_connections) entfernt — Strangler-Fig abgeschlossen.
// Netzwerk-Verwaltung läuft über /netzwerk via network.service.ts + organization_partnerships.

// ============================================================================
// EXPORT-ZONE: PDF / FHIR / GDT
// Erzeugt Payloads — Transport (KIM) uebernimmt das PVS.
// Siehe: docs/architecture/REFERRAL_COMPLIANCE_BOUNDARY.md
// ============================================================================

/**
 * Laedt Referral-Daten und erzeugt ein vollstaendiges FHIR R4 Bundle.
 * Loggt den Export in `referral_export_events` + `referral_audit_log`.
 */
export async function exportReferralAsFhir(
    referralId: string,
    target: ReferralExportTarget = "download",
): Promise<{ bundle: FhirReferralBundle; exportEventId: string }> {
    const sb = requireSupabase();

    const {
        data: { user },
    } = await sb.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    const { data: ref, error: refErr } = await sb
        .from("referrals")
        .select(
            `
            *,
            patient:patients!referrals_patient_id_fkey(id, first_name, last_name, date_of_birth, gender),
            source_doctor:doctors!referrals_source_doctor_id_fkey(id, title, first_name, last_name),
            target_doctor:doctors!referrals_target_doctor_id_fkey(id, title, first_name, last_name),
            source_practice:practices!referrals_source_practice_id_fkey(id, name, phone, email, address, city, zip),
            target_practice:practices!referrals_target_practice_id_fkey(id, name, phone, email, address, city, zip),
            appointment:appointments!referrals_booked_appointment_id_fkey(id, start_time, end_time)
            `,
        )
        .eq("id", referralId)
        .single();

    if (refErr || !ref) throw new Error(`Referral not found: ${refErr?.message ?? referralId}`);

    const patient = ref.patient as {
        id: string;
        first_name: string;
        last_name: string;
        date_of_birth?: string;
        gender?: string;
    } | null;
    const srcDoc = ref.source_doctor as {
        id: string;
        title?: string;
        first_name: string;
        last_name: string;
    } | null;
    const tgtDoc = ref.target_doctor as {
        id: string;
        title?: string;
        first_name: string;
        last_name: string;
    } | null;
    const srcPractice = ref.source_practice as {
        id: string;
        name: string;
        phone?: string;
        email?: string;
        address?: string;
        city?: string;
        zip?: string;
    } | null;
    const tgtPractice = ref.target_practice as {
        id: string;
        name: string;
        phone?: string;
        email?: string;
        address?: string;
        city?: string;
        zip?: string;
    } | null;
    const appt = ref.appointment as {
        id: string;
        start_time?: string;
        end_time?: string;
    } | null;

    const bundleInput: ReferralBundleInput = {
        referralId: ref.id,
        referralNumber: ref.referral_number ?? `REF-${ref.id.slice(0, 8)}`,
        status: ref.status,
        urgency: ref.urgency,
        specialty: ref.specialty,
        diagnosisText: ref.diagnosis_text,
        diagnosisCode: ref.diagnosis_code ?? undefined,
        clinicalQuestion: ref.clinical_question ?? undefined,
        referralReason: ref.referral_reason ?? undefined,
        relevantFindings: ref.relevant_findings ?? undefined,
        currentMedications: ref.current_medications ?? undefined,
        allergies: ref.allergies ?? undefined,
        validUntil: ref.valid_until,
        patientId: patient?.id ?? ref.patient_id,
        patientFirstName: patient?.first_name ?? "Unbekannt",
        patientLastName: patient?.last_name ?? "Unbekannt",
        patientBirthDate: patient?.date_of_birth ?? undefined,
        patientGender:
            (patient?.gender as "male" | "female" | "other" | "unknown") ?? undefined,
        sourceDoctorId: srcDoc?.id,
        sourceDoctorTitle: srcDoc?.title,
        sourceDoctorFirstName: srcDoc?.first_name,
        sourceDoctorLastName: srcDoc?.last_name,
        sourcePracticeId: srcPractice?.id ?? ref.source_practice_id,
        sourcePracticeName: srcPractice?.name ?? "Unbekannt",
        sourcePracticePhone: srcPractice?.phone,
        sourcePracticeEmail: srcPractice?.email,
        sourcePracticeAddress: srcPractice?.address,
        sourcePracticeCity: srcPractice?.city,
        sourcePracticeZip: srcPractice?.zip,
        targetDoctorId: tgtDoc?.id,
        targetDoctorTitle: tgtDoc?.title,
        targetDoctorFirstName: tgtDoc?.first_name,
        targetDoctorLastName: tgtDoc?.last_name,
        targetPracticeId: tgtPractice?.id ?? ref.target_practice_id ?? undefined,
        targetPracticeName: tgtPractice?.name,
        targetPracticePhone: tgtPractice?.phone,
        targetPracticeEmail: tgtPractice?.email,
        targetPracticeAddress: tgtPractice?.address,
        targetPracticeCity: tgtPractice?.city,
        targetPracticeZip: tgtPractice?.zip,
        appointmentId: appt?.id,
        appointmentStart: appt?.start_time,
        appointmentEnd: appt?.end_time,
    };

    const bundle = buildReferralFhirBundle(bundleInput);

    // Payload-Hash (SHA-256)
    const payloadJson = JSON.stringify(bundle);
    const encoder = new TextEncoder();
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(payloadJson));
    const payloadHash = Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");

    const { data: exportEvent, error: exportErr } = await sb
        .from("referral_export_events")
        .insert({
            referral_id: referralId,
            export_format: "fhir_r4" as ReferralExportFormat,
            export_target: target,
            payload_hash: payloadHash,
            exported_by: user.id,
        })
        .select("id")
        .single();

    if (exportErr) {
        logger.error("[ReferralService] exportReferralAsFhir log error:", exportErr);
        throw new Error(`Export-Logging fehlgeschlagen: ${exportErr.message}`);
    }

    return { bundle, exportEventId: exportEvent.id };
}

/**
 * Markiert einen Export-Event als "bereit fuer KIM-Versand".
 * MediFlow versendet NICHT selbst ueber KIM.
 */
export async function markExportAsKimReady(exportEventId: string): Promise<void> {
    const sb = requireSupabase();

    const {
        data: { user },
    } = await sb.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    const { error } = await sb
        .from("referral_export_events")
        .update({ delivery_channel: "kim_queued" })
        .eq("id", exportEventId)
        .eq("exported_by", user.id);

    if (error) {
        logger.error("[ReferralService] markExportAsKimReady error:", error);
        throw new Error(`KIM-Ready-Markierung fehlgeschlagen: ${error.message}`);
    }
}

/**
 * Laedt alle Export-Events fuer ein Referral (Audit-Trail).
 */
export async function getReferralExportEvents(
    referralId: string,
): Promise<ReferralExportEvent[]> {
    const sb = requireSupabase();

    const { data, error } = await sb
        .from("referral_export_events")
        .select(
            "id, referral_id, export_format, export_target, delivery_channel, payload_hash, exported_at, exported_by",
        )
        .eq("referral_id", referralId)
        .order("exported_at", { ascending: false });

    if (error) {
        logger.error("[ReferralService] getReferralExportEvents error:", error);
        throw new Error(`Export-Events laden fehlgeschlagen: ${error.message}`);
    }

    return (data ?? []).map((row) => ({
        id: row.id,
        referralId: row.referral_id,
        exportFormat: row.export_format as ReferralExportFormat,
        exportTarget: row.export_target as ReferralExportTarget,
        deliveryChannel: row.delivery_channel as "kim_queued" | null,
        payloadHash: row.payload_hash,
        exportedAt: row.exported_at,
        exportedBy: row.exported_by,
    }));
}

// ─── REFERRAL FUNNEL STATS ──────────────────────────────────

export interface ReferralFunnelStep {
    stage: string;
    count: number;
}

export interface ReferralFunnelPartner {
    partnerPracticeId: string;
    partnerName: string;
    direction: "outgoing" | "incoming";
    total: number;
    booked: number;
    completed: number;
    avgDaysToBooking: number | null;
}

export interface ReferralFunnelStats {
    funnel: {
        total: number;
        created: number;
        sent: number;
        booked: number;
        confirmed: number;
        completed: number;
        cancelled: number;
        rejected: number;
        expired: number;
        avgDaysToBooking: number | null;
    };
    byPartner: ReferralFunnelPartner[];
    periodDays: number;
}

/**
 * Holt aggregierte Referral-Funnel-Metriken pro Praxis
 */
export async function getReferralFunnelStats(
    practiceId: string,
    periodDays = 90,
): Promise<ReferralFunnelStats | null> {
    if (!isSupabaseConfigured()) return null;
    try {
        const { data, error } = await supabase.rpc("get_referral_funnel_stats", {
            p_practice_id: practiceId,
            p_days: periodDays,
        });

        if (error) throw error;

        const row = data as Record<string, unknown> | null;
        if (!row) return null;

        const funnel = row.funnel as Record<string, unknown> | null;
        const byPartnerRaw = row.by_partner as Record<string, unknown>[] | null;

        return {
            funnel: {
                total: Number(funnel?.total ?? 0),
                created: Number(funnel?.created ?? 0),
                sent: Number(funnel?.sent ?? 0),
                booked: Number(funnel?.booked ?? 0),
                confirmed: Number(funnel?.confirmed ?? 0),
                completed: Number(funnel?.completed ?? 0),
                cancelled: Number(funnel?.cancelled ?? 0),
                rejected: Number(funnel?.rejected ?? 0),
                expired: Number(funnel?.expired ?? 0),
                avgDaysToBooking: funnel?.avg_days_to_booking != null
                    ? Number(funnel.avg_days_to_booking)
                    : null,
            },
            byPartner: (byPartnerRaw ?? []).map((p) => ({
                partnerPracticeId: p.partner_practice_id as string,
                partnerName: p.partner_name as string,
                direction: p.direction as "outgoing" | "incoming",
                total: Number(p.total ?? 0),
                booked: Number(p.booked ?? 0),
                completed: Number(p.completed ?? 0),
                avgDaysToBooking: p.avg_days_to_booking != null
                    ? Number(p.avg_days_to_booking)
                    : null,
            })),
            periodDays: Number(row.period_days ?? periodDays),
        };
    } catch (e) {
        logger.error("referral.getReferralFunnelStats", e);
        return null;
    }
}
